import React, { Component, useState } from "react"
import { getSectionInfoSAT, SAT_SECTION_MODULE_KEYS, saveComputerBasedTestAnswer, saveAllComputerBasedTestAnswers, saveHighlightForQuestion, updateRemainingTimeForModule, saveComputerBasedTestFillInAnswer, findNextModuleNameKeyForSAT } from "../../../../services/utils/practice-test-util"
import CountdownTimer from "../../../../components/CountdownTimer"
import Button from "../../../../components/Button/Button"
import Calculator from "../../../../components/Calculator"
import Directions from "./Directions"
import Reference from "./Reference"
import LineReader from "../../../../components/LineReader"
import QuestionSAT from "./QuestionSAT"
import {MiniReview, MainReviewPage} from "./pages/ReviewPages"
import { NA } from "../../../../constants"
import "./ComputerTestSAT.css"

interface Props {
    userInfo: any
    testDetails: any
    onNext: () => void
    onTestExit: () => void
    currentModuleNameKey: string
    onPageUnmount?: () => void
}

interface State {
    currentQuestionIndex: number
    maxQuestions: number
    disableBack: boolean
    disableNext: boolean
    calculatorVisible: boolean
    referenceVisible: boolean
    directionsVisible: boolean
    lineReaderVisible: boolean
    highligthInfoVisible: boolean
    answeredQuestions: any[]
    currentQuestions: any[]
    computerTestCapabilities: any
    remainingTimeInSeconds: number
    miniReviewPageVisible: boolean
    mainReviewPageVisible: boolean
    questionInfoButtonVisible: boolean
}

/**
 * Computer-based Virtual SAT Practice Test.
 * This component emulates the official SAT practice test desktop application as much as possible.
 * Also, please note that this component is NOT to be used in a mobile phone.
 * 
 * Notes:
 *  + Timer starts right away
 *  + Directions for the test is visible for the user (timer still continues)
 *  + There are components such as Calculator and Reference available based on the test type
 */
export default class ComputerTestSAT extends Component<Props, State> {
    constructor(props) {
        super(props)
        this.state = {
            currentQuestionIndex: 0,
            currentQuestions: [],
            maxQuestions: 0,
            disableBack: true,
            disableNext: false,
            directionsVisible: true, // Initially visible
            calculatorVisible: false,
            referenceVisible: false,
            lineReaderVisible: false,
            highligthInfoVisible: false,
            miniReviewPageVisible: false,
            mainReviewPageVisible: false,
            questionInfoButtonVisible: true,
            answeredQuestions: [],
            computerTestCapabilities: {},
            remainingTimeInSeconds: -1,
        }
        this.initialize()
    }

    componentDidMount() {
        window.addEventListener("beforeunload", this.handlePageUnload)
        window.addEventListener("popstate", this.handlePopstate)
        this.initialize()
    }

    componentWillUnmount() {
        window.removeEventListener("beforeunload", this.handlePageUnload)
        setTimeout(() => {
            window.removeEventListener("popstate", this.handlePopstate)
        }, 500)

        let {onPageUnmount} = this.props

        if (onPageUnmount) {
            onPageUnmount()
        }
    }

    componentDidUpdate(prevProps: Readonly<Props>) {
        if (prevProps.currentModuleNameKey !== this.props.currentModuleNameKey) {
            this.initialize()
        }
    }

    initialize = () => {
        let {testDetails, currentModuleNameKey} = this.props
        let {questions, remainingTimeInSeconds, testTimeIndicator, currentQuestionIndex} = testDetails

        currentModuleNameKey = currentModuleNameKey ? currentModuleNameKey: SAT_SECTION_MODULE_KEYS.RW_MODULE1

        let {computerTestCapabilities} = getSectionInfoSAT(currentModuleNameKey, testDetails)
        let questionsForModule = questions[currentModuleNameKey]
        let maxQuestions = questionsForModule.length
        let duration = computerTestCapabilities.testDurationInSeconds[testTimeIndicator]

        remainingTimeInSeconds = remainingTimeInSeconds !== -1 ? remainingTimeInSeconds: duration
        this.setState({
            maxQuestions, 
            currentQuestions: questionsForModule, 
            computerTestCapabilities, 
            remainingTimeInSeconds, 
            currentQuestionIndex
        })
    }

    // Page unload detected!
    handlePageUnload = () => {
        this.submitTestDetails(false)
    }

    // Browser back button navigation detected!
    handlePopstate = () => {
        this.submitTestDetails(false)
    }

    handleBack = () => {
        let {currentQuestionIndex, disableBack, mainReviewPageVisible} = this.state

        if (mainReviewPageVisible) {
            this.setState({mainReviewPageVisible: false, questionInfoButtonVisible: true})
            return
        }

        --currentQuestionIndex

        if (currentQuestionIndex <= 0) {
            currentQuestionIndex = 0
            disableBack = true
        }

        this.setState({currentQuestionIndex, disableBack, disableNext: false})
    }

    handleNext = () => {
        let {currentQuestionIndex, maxQuestions, disableNext, mainReviewPageVisible, remainingTimeInSeconds} = this.state
        let {onNext, testDetails, currentModuleNameKey} = this.props

        ++currentQuestionIndex

        if (mainReviewPageVisible) {
            this.submitModuleCompletion()
            onNext()
        }

        if (currentQuestionIndex >= maxQuestions) {
            this.setState({mainReviewPageVisible: true})
        }
        else {
            this.setState({currentQuestionIndex, disableBack: false, disableNext})
        }
    }

    toggleCalculatorVisibility = () => {
        let {calculatorVisible} = this.state
        this.setState({calculatorVisible: !calculatorVisible})
    }

    toggleReferenceVisibility = () => {
        let {referenceVisible} = this.state
        this.setState({referenceVisible: !referenceVisible})
    }

    toggleLineReader = () => {
        let {lineReaderVisible} = this.state
        this.setState({lineReaderVisible: !lineReaderVisible})
    }

    toggleDirectionsVisibilty = () => {
        let {directionsVisible} = this.state
        this.setState({directionsVisible: !directionsVisible})
    }

    toggleHighlight = (over: boolean) => {
        // Currently just show a tooltip-like info box!
        this.setState({highligthInfoVisible: over})
    }

    closeCalculator = () => {
        this.setState({calculatorVisible: false})
    }

    closeReference = () => {
        this.setState({referenceVisible: false})
    }

    openDirections = () => {
        this.setState({directionsVisible: true})
    }

    closeDirections = () => {
        this.setState({directionsVisible: false})
    }

    closeLineReader = () => {
        this.setState({lineReaderVisible: false})
    }

    handleTimerTick = (remainingTimeInSeconds) => {
        // let {currentModuleNameKey} = this.props
        // let {currentQuestionIndex} = this.state
        // updateRemainingTimeForModule({currentModuleNameKey, currentQuestionIndex, remainingTimeInSeconds})
        this.setState({remainingTimeInSeconds})
    }

    handleTimeFinish = () => {
        let {onNext} = this.props

        this.submitTestDetails(true)

        if (onNext) {
            onNext()
        }
    }

    handleTestExit = () => {
        let {onTestExit} = this.props

        this.submitTestDetails(false)

        if (onTestExit) {
            onTestExit()
        }
    }

    submitTestDetails = (dueToTimeOut: boolean) => {
        let {testDetails, currentModuleNameKey} = this.props
        let {remainingTimeInSeconds, currentQuestionIndex} = this.state

        testDetails.remainingTimeInSeconds = remainingTimeInSeconds
        testDetails.currentQuestionIndex = currentQuestionIndex
        testDetails.currentModuleNameKey = currentModuleNameKey

        if (dueToTimeOut) {
            let nextModuleNameKey = findNextModuleNameKeyForSAT(currentModuleNameKey)

            if (nextModuleNameKey !== NA) {
                testDetails.currentModuleNameKey = nextModuleNameKey
            }
            else {
                testDetails.currentModuleNameKey = ""
                testDetails.completedTime = new Date().toISOString()
            }
            testDetails.remainingTimeInSeconds = -1
            testDetails.currentQuestionIndex = 0
        }

        saveAllComputerBasedTestAnswers(testDetails)
    }

    submitModuleCompletion = () => {
        let {testDetails, currentModuleNameKey} = this.props
        let nextModuleNameKey = findNextModuleNameKeyForSAT(currentModuleNameKey)

        if (nextModuleNameKey !== NA) {
            testDetails.currentModuleNameKey = nextModuleNameKey
        }
        else {
            testDetails.currentModuleNameKey = ""
            testDetails.completedTime = new Date().toISOString()
        }
        testDetails.remainingTimeInSeconds = -1
        testDetails.currentQuestionIndex = 0
        saveAllComputerBasedTestAnswers(testDetails)
    }

    submitAnswer = (answeredQuestion, fillIn=false) => {
        let {currentQuestions} = this.state
        let {testDetails, currentModuleNameKey} = this.props
        let {testName} = testDetails
        if (!fillIn) {
            saveComputerBasedTestAnswer(testName, currentModuleNameKey, answeredQuestion)
        }
        else {
            saveComputerBasedTestFillInAnswer(testName, currentModuleNameKey, answeredQuestion)
        }
        this.setState({currentQuestions})
    }

    handleAnswer = (question, answer: string) => {
        question.userSubmittedAnswer = answer
        this.submitAnswer(question)
    }

    handleFillInAnswer = (question, fillInAnswer: string,) => {
        question.fillInAnswer = fillInAnswer
        this.submitAnswer(question, true)
    }

    handleBookmark = (question) => {
        question.bookmarked = !question.bookmarked // Toggle bookmark
        this.submitAnswer(question)
    }

    handleHighlight = (question) => {
        saveHighlightForQuestion(question)
    }

    onToggleMiniReview = () => {
        this.setState({miniReviewPageVisible: !this.state.miniReviewPageVisible})
    }

    closeMiniReview = () => {
        this.setState({miniReviewPageVisible: false})
    }

    gotoQuestion = (questionNumber) => {
        let questionIndex = questionNumber - 1 // 0-based index
        let disableBack = questionIndex <= 0 ? true: false
        this.setState({currentQuestionIndex: questionIndex, 
            miniReviewPageVisible: false, 
            mainReviewPageVisible: false, 
            disableBack, 
            questionInfoButtonVisible: true
        })
    }

    showMainReviewPage = () => {
        this.setState({mainReviewPageVisible: true, miniReviewPageVisible: false, questionInfoButtonVisible: false})
    }

    render() {
        let {userInfo, testDetails, currentModuleNameKey} = this.props
        let {calculatorVisible, directionsVisible, referenceVisible, lineReaderVisible, highligthInfoVisible, remainingTimeInSeconds, miniReviewPageVisible} = this.state

        if (!testDetails) return null

        let isDirectionsOpen = directionsVisible

        let {firstName, lastName} = userInfo
        let {currentQuestionIndex, disableBack, disableNext, mainReviewPageVisible, questionInfoButtonVisible} = this.state
        let questionsForModule = testDetails.questions[currentModuleNameKey]
        let data = { userName: `${firstName} ${lastName}`, questionCurrent: currentQuestionIndex + 1, questionMax: questionsForModule.length}
        let calculatorClass = calculatorVisible ? "" : "hide"
        let referenceClass = referenceVisible ? "" : "hide"
        let lineReaderClass = lineReaderVisible ? "" : "hide"
        let {sectionTitle, testTitle, computerTestCapabilities} = getSectionInfoSAT(currentModuleNameKey, testDetails)
        let reviewTitle = `${sectionTitle} Questions`

        if (mainReviewPageVisible) {
            disableBack = false
        }

        return (
            <div className="computer-test-sat">
                <SectionTop currentModuleNameKey={currentModuleNameKey} 
                    sectionTitle={sectionTitle}
                    testTitle={testTitle}
                    computerTestCapabilities={computerTestCapabilities}
                    remainingTimeInSeconds={remainingTimeInSeconds}
                    onToggleDirections={this.toggleDirectionsVisibilty}
                    onToggleCalculator={this.toggleCalculatorVisibility}
                    onToggleReference={this.toggleReferenceVisibility}
                    onToggleLineReader={this.toggleLineReader}
                    onToggleHighlight={this.toggleHighlight}
                    onTestExit={this.handleTestExit}
                    onTimerTick={this.handleTimerTick}
                    onTimeFinish={this.handleTimeFinish}
                    isDirectionsOpen={isDirectionsOpen}
                />
                <SectionMiddle 
                    questions={questionsForModule} 
                    questionIndex={currentQuestionIndex}
                    computerTestCapabilities={computerTestCapabilities}
                    onAnswer={this.handleAnswer}
                    onFillInAnswer={this.handleFillInAnswer}
                    onBookmark={this.handleBookmark}
                    onHighlight={this.handleHighlight}
                    onGotoQuestion={this.gotoQuestion}
                    reviewTitle={reviewTitle}
                    mainReviewPageVisible={mainReviewPageVisible}
                />
                <SectionBottom 
                    data={data} 
                    onBack={this.handleBack}
                    onNext={this.handleNext}
                    onToggleMiniReview={this.onToggleMiniReview}
                    disableBack={disableBack}
                    disableNext={disableNext}
                    questionInfoButtonVisible={questionInfoButtonVisible}
                />

                { directionsVisible &&
                    <Directions currentModuleNameKey={currentModuleNameKey} onClose={this.closeDirections} />
                }

                { computerTestCapabilities.calculator &&
                    <Calculator draggable={true} onClose={this.closeCalculator} calculatorClass={calculatorClass}/>
                }

                { computerTestCapabilities.reference &&
                    <Reference draggable={true} onClose={this.closeReference} customClass={referenceClass}/>
                }

                <LineReader draggable={true} onClose={this.closeLineReader} customClass={lineReaderClass}/>

                { highligthInfoVisible && <HighlighInfo /> }

                { miniReviewPageVisible && 
                    <MiniReview 
                        title={reviewTitle} 
                        questions={questionsForModule}
                        currentQuestionIndex={currentQuestionIndex}
                        onClose={this.closeMiniReview}
                        onGotoQuestion={this.gotoQuestion}
                        onGotoReviewPage={this.showMainReviewPage}
                    />
                }
            </div>
        )
    }
}

//--- Helpers ---
function SectionTop(props) {
    let {currentModuleNameKey, sectionTitle, testTitle: testInfo, remainingTimeInSeconds, 
        onToggleCalculator, onToggleReference, onToggleDirections, onToggleLineReader, onToggleHighlight, onTimerTick, onTimeFinish, onTestExit,
        computerTestCapabilities, isDirectionsOpen} = props
    let durationInSeconds = remainingTimeInSeconds

    return (
        <>
        <div className="section-top">
            <div className="section-info align-items-end">
                    <div className="section-info-item w-100 w-md-auto">
                        <h5 className="section-info-title mb-0">{sectionTitle}</h5>
                        <button className={`d-none d-md-inline-block btn btn-simple btn--directions ${isDirectionsOpen ? 'is-open' : ''}`} onClick={onToggleDirections}>
                            Directions
                            <span className="collapsable-card__collapse-btn"></span>
                        </button>
                    </div>
                    <div className="section-info-item d-md-none">
                        <button className={`btn btn-simple btn--directions ${isDirectionsOpen ? 'is-open' : ''}`} onClick={onToggleDirections}>
                            Directions
                            <span className="collapsable-card__collapse-btn"></span>
                        </button>
                    </div>
                    <div className="section-info-item 0 ml-auto mr-3 mx-md-0">
                        <CountdownTimer countdownForModule={currentModuleNameKey} fromInSeconds={durationInSeconds} onTimerTick={onTimerTick} onFinish={onTimeFinish}/>
                    </div>
                    <div className="section-info-item d-flex justify-content-end">
                        { computerTestCapabilities.calculator &&
                            <div>
                                <button title="Calculator" className="btn-simple mr-3" onClick={onToggleCalculator}>
                                    <img
                                        alt="Calculator"
                                        width="16"
                                        height="20"
                                        src="/assets/images/icons/v2/ico-calculator.svg" />
                                    <br/>
                                    <span className="d-none d-md-block">Calculator</span>
                                </button>
                            </div>
                        }
                        { computerTestCapabilities.reference &&
                            <div>
                                <button title="Reference" className="btn-simple mr-3" onClick={onToggleReference}>
                                    <img
                                        alt="Reference"
                                        width="16"
                                        height="20"
                                        src="/assets/images/icons/v2/ico-reference.svg" />
                                    <br/>
                                    <span className="d-none d-md-block">Reference</span>
                                </button>
                            </div>
                        }
                        { computerTestCapabilities.highlight &&
                            <div id="highlights-and-notes">
                                <button title="Highlights" className="btn-simple mr-3" onMouseOver={e => onToggleHighlight(true)} onMouseLeave={e => onToggleHighlight(false)}>
                                    <img
                                        alt="Highlights"
                                        width="16"
                                        height="20"
                                        src="/assets/images/icons/v2/ico-highlighter.svg" />
                                    <br/>
                                    <span className="d-none d-md-block">Highlights</span>
                                </button>
                            </div>
                        }

                        <div className="menu-dropdown-item">
                            <button title="More" className="btn-simple">
                                <img
                                    alt="More"
                                    width="20"
                                    height="20"
                                    src="/assets/images/icons/v2/ico-more-vertical.svg" />
                                <br/>
                                <span className="d-none d-md-block">More</span>
                                <div className="more-menu-dropdown">
                                    <div className="mb-3">
                                        <button title="Line Reader" className="btn-simple" onClick={onToggleLineReader}>
                                            <img
                                            alt="Line Reader"
                                            className="mr-2 smallIcon"
                                            width="14"
                                            height="20"
                                            src="/assets/images/icons/v2/ico-line-reader-outline.svg" />
                                            <span>Line Reader</span>
                                        </button>
                                    </div>
                                    <div>
                                        <button title="Save &amp; Exit" className="btn-simple" onClick={onTestExit}>
                                        <img
                                            alt="Save &amp; Exit"
                                            className="mr-2"
                                            width="16"
                                            height="20"
                                            src="/assets/images/icons/v2/ico-save-exit-outline.svg" />
                                            <span>Save &amp; Exit</span>
                                        </button>
                                    </div>
                                </div>
                            </button>
                        </div>
                    </div>
            </div>
        </div>
        <div className="section-subinfo ">
            <div className="test-info">
                {testInfo}
            </div>
        </div>
    </>
    )
}

function SectionMiddle(props) {
    let {questions=[], questionIndex=0, onAnswer, onFillInAnswer, onBookmark, onHighlight, onGotoQuestion, mainReviewPageVisible, reviewTitle, computerTestCapabilities} = props
    let {highlight} = computerTestCapabilities
    let question = questions[questionIndex]

    return (
        <div className="section-middle">
            {!mainReviewPageVisible ?
                <QuestionSAT
                    question={question}
                    highlighterEnabled={highlight}
                    onAnswer={onAnswer}
                    onFillInAnswer={onFillInAnswer}
                    onBookmark={onBookmark}
                    onHighlight={onHighlight}
                />
                :
                <MainReviewPage title={reviewTitle} questions={questions} onGotoQuestion={onGotoQuestion}/>
            }
        </div>
    )
}

function SectionBottom(props) {
    let {data, onBack, onNext, disableBack, disableNext, onToggleMiniReview, questionInfoButtonVisible} = props
    let {userName, questionCurrent, questionMax} = data
    let [isOpen, setIsOpen] = useState(false);
    let handleToggle = () => {
        setIsOpen(o => !o);
        onToggleMiniReview && onToggleMiniReview();
    }

    return (
        <div className="section-bottom">
            <div className="bottom-left">
                {userName}
            </div>
            <div className="bottom-middle">
                {questionInfoButtonVisible &&
                    <button className={`btn btn--bg-black btn--text-white btn-toggle-end` + (isOpen ? " is-open" : "")} onClick={handleToggle}>
                        Question {questionCurrent} of {questionMax} <span className="collapsable-card__collapse-btn"></span>
                    </button>
                }
            </div>
            <div className="bottom-right">
                {!disableBack && <Button text={"Back"} onClick={onBack} disabled={disableBack}/>}
                <Button text={"Next"} onClick={onNext} disabled={disableNext}/>
            </div>
        </div>
    )
}

function HighlighInfo(props) {
    let elm = document.getElementById("highlights-and-notes")
    let rec = elm?.getBoundingClientRect()
    let left = rec ? rec.left - 75: 10
    let top = rec ? rec.top + 50: 10

    return (
        <div className="highlight-info" style={{left: left, top: top}}>
            <h1>Highlight mode on:</h1>
            <p>
                Select text to create a highlight automatically.
            </p>
        </div>
    )
}
